/**
 * The Overview's price-card panel (TMMAPI-0131): the adopted granted card, read-only, with its
 * price and margin. No adopted card points an owner or admin at Your price cards.
 */
import { useId } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { rateCardsApi, type GrantedCard } from '@/api/rateCards';
import { useCanWrite } from '@/auth/authHooks';
import { moneyIn } from '@/components/moneyIn';
import { useRemote } from '@/components/useRemote';
import { useLocale } from '@/i18n/useLocale';

const load = () => rateCardsApi.listGrantedCards();

export function RateCardPanel() {
  const { t } = useTranslation();
  const locale = useLocale();
  const canWrite = useCanWrite();
  const titleId = useId();
  const { status, data: cards, reload } = useRemote(load);
  const dash = t('statements.unavailable');
  const adopted = cards?.find((card) => card.adopted);

  return (
    <Paper component="section" aria-labelledby={titleId} sx={{ p: 2.5, mb: 2.5 }}>
      <Typography id={titleId} variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {t('rateCard.panel.title')}
      </Typography>

      {status === 'error' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              {t('common.retry')}
            </Button>
          }
        >
          {t('rateCard.panel.loadFailed')}
        </Alert>
      ) : null}

      {cards === null ? (
        status === 'loading' ? (
          <CircularProgress size={20} aria-label={t('common.loading')} />
        ) : null
      ) : adopted ? (
        <AdoptedCard card={adopted} dash={dash} locale={locale} />
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2">{t('rateCard.panel.none')}</Typography>
          {canWrite ? (
            <Button component={RouterLink} to="/rate-cards">
              {t('pages.rateCards.title')}
            </Button>
          ) : null}
        </Box>
      )}
    </Paper>
  );
}

function AdoptedCard({
  card,
  dash,
  locale,
}: {
  card: GrantedCard;
  dash: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const price = moneyIn(card.baseMinor, card.currency, locale, dash);
  const margin =
    card.marginMinor === null ? dash : moneyIn(card.marginMinor, card.currency, locale, dash);
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Link component={RouterLink} to={`/rate-cards/${card.id}`} variant="body2" sx={{ fontWeight: 500 }}>
        {card.name || t('rateCard.panel.unknown')}
      </Link>
      <Typography variant="body2">{t('rateCard.panel.basePerMonth', { amount: price })}</Typography>
      <Typography variant="body2">{t('rateCard.panel.margin', { amount: margin })}</Typography>
    </Box>
  );
}
